import { Link } from "react-router-dom";
import type { PersonProfile } from "@sales-ops-amplify/shared";
import { cn } from "@/cn";

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[parts.length - 1][0]}`.toUpperCase();
}

export const PERSON_TABS = [
  { id: "overview", label: "Overview" },
  { id: "activities", label: "Activities" },
  { id: "sequences", label: "Sequences" },
  { id: "conversations", label: "Conversations" },
  { id: "enrichment", label: "Enrichment" },
  { id: "fields", label: "All Fields" },
] as const;

export type PersonTabId = (typeof PERSON_TABS)[number]["id"];

export function isPersonTabId(value: string | undefined): value is PersonTabId {
  return PERSON_TABS.some((t) => t.id === value);
}

export function PersonHero({
  profile,
  isMutable,
  peopleBase,
  companyHref,
  onSave,
  onEnrich,
  onAddToList,
  onAddToSequence,
  saving,
  enriching,
}: {
  profile: PersonProfile;
  isMutable: boolean;
  peopleBase: "/people" | "/user/leads";
  companyHref?: string;
  onSave?: () => void;
  onEnrich?: () => void;
  onAddToList?: () => void;
  onAddToSequence?: () => void;
  saving?: boolean;
  enriching?: boolean;
}) {
  const location = [profile.city, profile.state, profile.country]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="min-w-0 max-w-full space-y-4 overflow-x-hidden border-b border-border pb-4">
      <nav className="flex min-w-0 items-center gap-1.5 text-sm text-muted-foreground">
        <Link
          to={peopleBase}
          className="shrink-0 hover:text-brand hover:underline"
        >
          {isMutable ? "Leads" : "Find people"}
        </Link>
        <span aria-hidden className="shrink-0">
          /
        </span>
        <span className="min-w-0 truncate text-foreground">{profile.name}</span>
      </nav>

      <div className="flex min-w-0 flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 max-w-full items-start gap-3">
          <div className="flex size-14 shrink-0 items-center justify-center rounded-full bg-brand/15 text-lg font-semibold text-brand">
            {initials(profile.name)}
          </div>
          <div className="min-w-0 max-w-full">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <h1 className="break-words text-2xl font-semibold tracking-tight text-foreground">
                {profile.name}
              </h1>
              {profile.linkedinUrl ? (
                <a
                  href={profile.linkedinUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="shrink-0 text-xs text-brand hover:underline"
                >
                  LinkedIn
                </a>
              ) : null}
            </div>
            <p className="mt-1 break-words text-sm text-muted-foreground">
              {profile.title ?? "—"}
              {profile.organization.name ? (
                <>
                  {" at "}
                  {companyHref ? (
                    <Link
                      to={companyHref}
                      className="break-words font-medium text-foreground hover:text-brand hover:underline"
                    >
                      {profile.organization.name}
                    </Link>
                  ) : (
                    <span className="break-words font-medium text-foreground">
                      {profile.organization.name}
                    </span>
                  )}
                </>
              ) : null}
              {location ? ` · ${location}` : ""}
            </p>
            {profile.stage ? (
              <span className="mt-2 inline-flex rounded-md border border-border bg-muted/40 px-2 py-0.5 text-[11px] font-medium text-muted-foreground">
                {profile.stage}
              </span>
            ) : null}
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {profile.email ? (
            <a
              href={`mailto:${profile.email}`}
              className="rounded-md border border-border px-2.5 py-1.5 text-xs font-medium hover:bg-muted/40"
            >
              Email
            </a>
          ) : null}
          {onAddToList ? (
            <button
              type="button"
              onClick={onAddToList}
              className="rounded-md border border-border px-2.5 py-1.5 text-xs font-medium hover:bg-muted/40"
            >
              Add to list
            </button>
          ) : null}
          {onAddToSequence ? (
            <button
              type="button"
              onClick={onAddToSequence}
              className="rounded-md border border-border px-2.5 py-1.5 text-xs font-medium hover:bg-muted/40"
            >
              Add to sequence
            </button>
          ) : null}
          {onEnrich ? (
            <button
              type="button"
              disabled={enriching}
              onClick={onEnrich}
              className="rounded-md border border-border px-2.5 py-1.5 text-xs font-medium hover:bg-muted/40 disabled:opacity-50"
            >
              {enriching ? "Enriching…" : "Enrich"}
            </button>
          ) : null}
          {!isMutable && !profile.alreadySaved && onSave ? (
            <button
              type="button"
              disabled={saving}
              onClick={onSave}
              className={cn(
                "rounded-md bg-brand px-2.5 py-1.5 text-xs font-medium text-brand-foreground disabled:opacity-50",
              )}
            >
              {saving ? "Saving…" : "Add to leads"}
            </button>
          ) : null}
          {profile.alreadySaved && !isMutable && profile.leadId ? (
            <Link
              to={`/user/leads/${profile.leadId}`}
              className="rounded-md bg-brand px-2.5 py-1.5 text-xs font-medium text-brand-foreground"
            >
              Open my copy
            </Link>
          ) : null}
        </div>
      </div>
    </div>
  );
}
